import React, {useEffect, useState} from 'react';
import './ProtoUI.css';
import {useStateCallback} from "../actions/UseStateCallback";
import axios from "axios";
import ReCAPTCHA from "react-google-recaptcha";
import Cookies from 'js-cookie';
// import Repeat from '../assets/icons/Repeat';
import Pens from "../assets/icons/Pens";
import FileLines from "../assets/icons/FileLines";

interface PopupWrapperInterface {
    onClickOpenPopup : () => void
}

const ProtoUI = (props: PopupWrapperInterface) => {
  const brandingMessage = "\"Geniebook just helped me improve my composition score and writing skills! Check out the personalised feedback and tips I got for one of my compositions. Try it out for yourself at https://geniebook.com/essay-marker!\""
  const [essayContent, setEssayContent] = useState<string>('');
  const [wordCount, setWordCount] = useState<number>(0);
  const [isSubmitted, setIsSubmitted] = useState<boolean>(false);
  const [disableRequest, setDisableRequest] = useState<boolean>(false);
  const [isVerified, setIsVerified] = useState<boolean>(false);
  const [isGenerating, setIsGenerating] = useState<boolean>(false);

  const [fullResult, setFullResult] = useStateCallback<string>('');
  const [totalScore, setTotalScore] = useStateCallback<number>(0);
  const [copyResultText, setCopyResultText] = useStateCallback<string>('');
  const [auth, setAuth] = useState<string>('');
  const [isError, setIsError] = useState<boolean>(false);
  const recaptchaRef = React.useRef<ReCAPTCHA>(null);

  useEffect(() => {
      const cookiesAuth = Cookies.get('gb_essay_marker')
      if (cookiesAuth === undefined || cookiesAuth === '') {
          setNewCookies()
      } else {
          setAuth(cookiesAuth)
      }
  }, []);

  const setNewCookies = () => {
      const params:any = {
          api_key: process.env.REACT_APP_ESSAY_MARKER_API_KEY,
      }
      const config_headers = {
          'Content-Type' : 'application/json',
      }
      axios.post(process.env.REACT_APP_ESSAY_MARKER_BACKEND_URI +'api/v1/access', params, {headers: config_headers})
          .then((r) => {
              const response = r.data?.token
              if (response !== undefined) {
                  Cookies.set('gb_essay_marker', response)
                  setAuth(response)
              }
          })
  }

  const reset = () => {
    setFullResult('')
  }


  const handleEssayContentChange = (event: React.ChangeEvent<HTMLTextAreaElement>) => {
    if (isSubmitted) {
      if (fullResult !== '') {
        setFullResult('')
        setIsVerified(false);
        recaptchaRef.current?.reset();
        setIsSubmitted(false);
        setIsError(false);
      }
    }
    let share_text = event.target.value.slice(0, 4095);
    let wc = event.target.value.split(/\s+/).length;
    if (event.target.value === '') wc = 0;
    
    if (wc < 150) {
      setDisableRequest(true);
    } else {
      setDisableRequest(false);
    }

    setWordCount(wc);
    setEssayContent(share_text);
  };

  // set disable if wordcount < 150
  const handleSubmit = (event: React.FormEvent) => {
    event.preventDefault();
    if (!isVerified) return;
    reset()
    setIsSubmitted(true);
    setCopyResultText('Copy Result');
    setIsGenerating(true);
    handleRequestBackend();
  };

  const handleRequestBackend = () => {
    const recaptchaValue = recaptchaRef.current?.getValue();

    const params:any = {
        essay_input: essayContent,
        num_words: wordCount,
        "g-recaptcha-response": recaptchaValue
    }

    const config_headers = {
        'Content-Type' : 'application/json',
        'Authorization': 'Bearer ' + auth,
    }

    axios.post(process.env.REACT_APP_ESSAY_MARKER_BACKEND_URI +'api/v1/engine', params, {headers: config_headers})
        .then((r) => {
          const response = r.data
          if (r.status === 200) {
            if (response.code === 200) {
              const payload =  response.payload
              setIsError(false)
              setTotalScore(payload.score)
              setFullResult(payload.essay_output)
              setAuth(response.token)
              Cookies.set('gb_essay_marker', response.token)
            }
          }
        }).catch((err) => {
            setIsError(true)
            const r = err.response
            if (err.code === 'ERR_NETWORK'){
                setTotalScore(0)
                setFullResult("Something went wrong. Please try again later.")
            } else {
                if (r.status === 401) {
                    setTotalScore(0)
                    setFullResult(r.data.message)
                } else {
                    setTotalScore(0)
                    setFullResult("Sorry, there was an error happening while processing your request. Please try again later.")
                }
            }
        }).finally(()=>{
            setIsGenerating(false);
            setDisableRequest(true);
            setIsVerified(false);
            recaptchaRef.current?.reset()
        })
  }

  const copyResult = () => {
    if (fullResult) {
      const contentToCopy = brandingMessage + "\n\n" +fullResult ;
      navigator.clipboard.writeText(contentToCopy)
        .then(() => {
          setCopyResultText('Copy Success!');
        })
        .catch(() => {
          setCopyResultText('Copy Failed');
        });
    }
  };

  const handleGenerateSample = () => {
      const config_headers = {
          'Content-Type' : 'application/json',
          'Authorization': 'Bearer ' + auth,
      }
      axios.get(process.env.REACT_APP_ESSAY_MARKER_BACKEND_URI +'api/v1/sample-composition', {headers: config_headers})
          .then((r) => {
              const response = r.data
              if (r.status === 200) {
                  if (response.code === 200) {
                      const payload =  response.payload
                      setEssayContent(payload.composition)
                      if (isSubmitted) {
                          if (fullResult !== '') {
                              setFullResult('')
                              setIsVerified(false);
                              recaptchaRef.current?.reset();
                              setIsSubmitted(false);
                              setIsError(false);
                          }
                      }
                      let share_text = payload.composition.slice(0, 4095);
                      let wc = payload.composition.split(/\s+/).length;
                      if (payload.composition.value === '') wc = 0;

                      if (wc < 150) {
                          setDisableRequest(true);
                      } else {
                          setDisableRequest(false);
                      }

                      setWordCount(wc);
                      setEssayContent(share_text);
                  }
              }
        })
  }

  return (
      <div className="flex-auto">
      <main className={`container "h-fit"`}>
          <div className="mt-[calc(56px)]">
            <h1 className="text-center text-[#262262] text-[40px] leading-[47px] font-['Museo'] font-extrabold">English Composition Grader</h1>
            <h2 className="text-center text-[#262262] text-[20px] leading-[20px] font-['Campton'] font-bold mt-[11px]">AI-generated feedback for P5 and P6 composition writing</h2>
          </div>
          <form onSubmit={handleSubmit} className="mt-[42px]">
              <div className="sm:float-right mb-[8px] h-[19px]">
                  <button type="button" className="button-field" onClick={handleGenerateSample}>
                      <FileLines className="h-[16px] mr-[5px] fill-current text-[#F7941E]"/>
                      <span className="font-black font-['Campton'] text-[16px] leading-[16px] text-[#F7941E]">Generate sample compositions</span>
                  </button>
              </div>
              <label htmlFor="result" className="block font-['Campton'] font-bold text-[#262262] text-[16px] leading-[19px] mb-[8px] float-left">Ready with your composition? Type here:</label>
              {/*<div className="max-md:hidden flex justify-center items-center float-right mb-[8px] h-[19px]">*/}
              {/*    <button type="button" className="button-field" onClick={handleGenerateSample}>*/}
              {/*        <Repeat className="h-[16px] mr-[5px] fill-current text-[#F7941E]"/>*/}
              {/*        <span className="font-black font-['Campton'] text-[16px] leading-[16px] text-[#F7941E]">Generate Sample</span>*/}
              {/*    </button>*/}
              {/*    <span className="text-[#F6C483] text-[16px] mr-[16px] ml-[16px]">|</span>*/}
              {/*    <button type="button" className="button-field" onClick={props.onClickOpenPopup}>*/}
              {/*            <Pens className="h-[16px] mr-[5px] fill-current text-[#F7941E]"/>*/}
              {/*            <span className="font-black font-['Campton'] text-[16px] leading-[16px] text-[#F7941E]">Handwritten</span>*/}
              {/*    </button>*/}
              {/*</div>*/}


              <div className="set-input-text">
                <textarea
                  id="essayContent"
                  value={essayContent}
                  onChange={handleEssayContentChange}
                  placeholder="Type your composition here..."
                  required
                />
                <p className={("float-left font-['Campton'] mx-1")}>Word Count: {wordCount} (Minimum 150 Words)</p>
              </div>

              <div className="md:float-left flex mb-[8px] h-[19px]">
                  <button type="button" className="button-field mr-[10px]" onClick={props.onClickOpenPopup}>
                      <Pens className="h-[16px] mr-[5px] fill-current text-[#F7941E]"/>
                      <span className="font-black font-['Campton'] text-[16px] leading-[16px] text-[#F7941E]">Try with handwritten work</span>
                  </button>
              </div>

              {/*<div className="md:hidden flex justify-center items-center mb-[8px] h-[19px]">*/}
              {/*    <button type="button" className="button-field" onClick={handleGenerateSample}>*/}
              {/*        <Repeat className="h-[16px] mr-[5px] fill-current text-[#F7941E]"/>*/}
              {/*        <span className="font-black font-['Campton'] text-[16px] leading-[16px] text-[#F7941E]">Generate Sample</span>*/}
              {/*    </button>*/}
              {/*    <span className="text-[#F6C483] text-[16px] mr-[16px] ml-[16px]">|</span>*/}
              {/*    <button type="button" className="button-field" onClick={props.onClickOpenPopup}>*/}
              {/*        <Pens className="h-[16px] mr-[5px] fill-current text-[#F7941E]"/>*/}
              {/*        <span className="font-black font-['Campton'] text-[16px] leading-[16px] text-[#F7941E]">Handwritten</span>*/}
              {/*    </button>*/}
              {/*</div>*/}

              <div className="md:float-right md:flex max-md:mt-5">
                {/* Recaptcha */}
                <ReCAPTCHA ref={recaptchaRef} size={'normal'} className={'g-recaptcha w-[210px]'} sitekey="6LfoCyglAAAAALP4yoSUh5iI8BovA-8fJOKGTncT" onChange={() => {setIsVerified(true)}} onExpired={() => {setIsVerified(false)}}></ReCAPTCHA>

                {/* Submit */}
                <button
                  type="submit"
                  className={`${isSubmitted? 'mb-[24px]' : 'mb-[calc(50px+20px)] max-md:mb-[calc(50px)]'} max-md:-mt-[10px]`}
                  disabled={((!essayContent || isSubmitted || disableRequest || !isVerified || isGenerating))}>
                    <div className={((!essayContent || isSubmitted || disableRequest || !isVerified ? " disabled " : "")) + "button-container"}>
                      <div className="overflow-hidden text-center transition cursor-pointer button button-orange button-shadow w-[164px] h-[48px]">
                        <div className="button_bg_hover"></div>
                        <div className="button_content text-[16px] text-['Campton'] font-extrabold relative leading-[48px]">
                          {disableRequest? "Get Feedback" : isSubmitted? "Generating..." : "Get Feedback"}
                        </div>
                      </div>
                    </div>
                </button>
              </div>
          </form>
              
              {/* Result here */}
              <div className={`${isSubmitted? "block" : "hidden"} set-output-text flex-row ${fullResult === '' ? 'mb-[calc(40px+15px)] max-sm:mb-[calc(75px+15px)]' : '' }`}>
                  <div id="outputContent2" className="right-result-side no-select">
                    <pre> 
                      {(fullResult === "" ? "Almost there, hold tight while we generate feedback for your Essay Language…" : fullResult)}
                      {/*{(fullResult === "" ? "" : (*/}
                      {/*  <a className="text-blue-500 underline hover:text-blue-700" href={baseUrl}>{baseUrl}</a>*/}
                      {/*))}*/}
                    </pre>
                       
                    <button className={`${isSubmitted? fullResult !== "" ? !isError ? "block" : "hidden" : "hidden" : "hidden"} float-right pb-[12px] hover:text-yellow-700`} onClick={copyResult}>
                       <div className="hover:opacity-75 active:text-blue-500">
                        <span className="copySVG" style={{
                          marginTop: "23px",
                          color: "#f7941e",
                        }}></span>
                        <span className="" style={{
                          color: "#f7941e",
                          textAlign: "left",
                          fontSize: "16px",
                          fontFamily: "Campton",
                          fontWeight: "400",
                        }}> {copyResultText} </span>
                       </div>
                    </button>
                  </div>
                  
                <span className="font-['Campton'] mx-1 no-select">Score</span>
                <br/><br/><br/>
                <button className={`float-left flex no-select`} onClick={copyResult}>
                  <p className="text-[#27AAE1] font-extrabold font-['Campton'] text-[32px] ml-[18px]">{(fullResult === "" ? "-" : String(totalScore))}</p>
                  <p className="text-[#27AAE1] font-extrabold font-['Campton'] text-[16px] mt-[14px]" >/20</p>
                </button>
              </div>

              {/* Copy Button */}
              {/* <button className={`${isSubmitted? fullResult !== "" ? "block" : "hidden" : "hidden"} float-right pb-4 mb-[calc(40px+15px)] max-sm:mb-[calc(75px+15px)]`} onClick={copyResult}>
                <div className="button-container">
                  <div className="overflow-hidden text-center transition cursor-pointer button button-orange button-shadow w-[164px] h-[48px]">
                    <div className="button_bg_hover"></div>
                    <div className="button_content text-[16px] text-['Campton'] font-extrabold relative leading-[48px]">
                      Copy Result
                    </div>
                  </div>
                </div>
              </button> */}
            </main>
      </div>
      );
};


export default ProtoUI;